import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone, Sparkles } from 'lucide-react';
import { servicesData } from '../data/mockData';
import './ServicesPage.funnel.css';

const CAL_URL = 'https://calendly.com/thecollinsacademynew/30min';

const ServicesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredServices = servicesData.filter(service =>
    !service.title.includes('Tradeline') &&
    !service.title.includes('AU ') &&
    !service.title.includes('ChexSystems') &&
    !service.title.toLowerCase().includes('guide') &&
    !service.title.toLowerCase().includes('affidavit') &&
    !service.title.includes('Wealth Rebuild') &&
    !service.title.includes('Million Dollar Software') &&
    !service.title.includes('Mentorship')
  );

  // Reorder: Strategy Call → Student Loan Removal → Complete Credit Repair → ...
  {
    const strategyIdx = filteredServices.findIndex((s) => s.title === '1-on-1 Strategy Call');
    const studentIdx = filteredServices.findIndex((s) => s.title === 'Student Loan Removal');
    if (strategyIdx !== -1 && studentIdx !== -1 && studentIdx !== strategyIdx + 1) {
      const [student] = filteredServices.splice(studentIdx, 1);
      const newStrategyIdx = filteredServices.findIndex((s) => s.title === '1-on-1 Strategy Call');
      filteredServices.splice(newStrategyIdx + 1, 0, student);
    }
    const newStrategyIdx = filteredServices.findIndex((s) => s.title === '1-on-1 Strategy Call');
    const repairIdx = filteredServices.findIndex((s) => s.title === 'Complete Credit Repair Service');
    const desiredRepairIdx = newStrategyIdx + 2;
    if (newStrategyIdx !== -1 && repairIdx !== -1 && repairIdx !== desiredRepairIdx) {
      const [repair] = filteredServices.splice(repairIdx, 1);
      filteredServices.splice(desiredRepairIdx, 0, repair);
    }
  }

  const shortenDescription = (description) => {
    if (!description) return '';
    if (description.length <= 240) return description;
    const truncated = description.substring(0, 240);
    const lastPeriod = truncated.lastIndexOf('.');
    if (lastPeriod > 100) return description.substring(0, lastPeriod + 1);
    return truncated.trim() + '...';
  };

  const flagship = ['1-on-1 Strategy Call', 'Student Loan Removal'];
  // These services get a "Read More" internal link instead of the external Buy Now checkout
  const readMoreTitles = ['Student Loan Removal', 'Complete Credit Repair Service'];

  return (
    <div className="sv-page" data-testid="services-page">
      {/* HERO */}
      <section className="sv-hero">
        <div className="sv-hero-inner">
          <span className="sv-eyebrow">
            <Sparkles size={14} /> The Collins Academy Services
          </span>
          <h1 className="sv-hero-title">
            Done-for-you solutions for every stage of your <span className="gold">wealth journey</span>.
          </h1>
          <p className="sv-hero-sub">
            Credit repair, student loan removal, funding, AI business systems, and 1-on-1 strategy —
            each stand-alone or bundled into the Collins Complete Stack.
          </p>
        </div>
      </section>

      {/* SERVICE GRID */}
      <section className="sv-grid-section">
        <div className="sv-grid">
          {filteredServices.map((service, i) => {
            const isFlagship = flagship.includes(service.title);
            return (
              <article
                key={service.id}
                className={`sv-card${isFlagship ? ' sv-card-flagship' : ''}`}
                data-testid={`service-card-${service.id}`}
              >
                {isFlagship && <span className="sv-ribbon">Most Requested</span>}

                <div className="sv-card-head">
                  <span className="sv-card-num">{String(i + 1).padStart(2, '0')}</span>
                  <h2 className="sv-card-title">{service.title}</h2>
                  {service.pricing && <div className="sv-card-pricing">{service.pricing}</div>}
                </div>

                <p className="sv-card-desc">{shortenDescription(service.description)}</p>

                {service.features && service.features.length > 0 && (
                  <ul className="sv-card-features">
                    {service.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx}>
                        <CheckCircle2 size={16} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="sv-card-actions">
                  {readMoreTitles.includes(service.title) && service.link && service.link.startsWith('/') ? (
                    <Link
                      to={service.link}
                      className="sv-btn sv-btn-primary"
                      data-testid={`read-more-${service.id}`}
                    >
                      Read More <ArrowRight size={16} />
                    </Link>
                  ) : service.paymentLink ? (
                    <a
                      href={service.paymentLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sv-btn sv-btn-primary"
                      data-testid={`buy-now-${service.id}`}
                    >
                      Buy Now <ArrowRight size={16} />
                    </a>
                  ) : service.link && service.link.startsWith('/') && service.link !== '/services' ? (
                    <Link
                      to={service.link}
                      className="sv-btn sv-btn-primary"
                      data-testid={`get-started-${service.id}`}
                    >
                      Get Started <ArrowRight size={16} />
                    </Link>
                  ) : (
                    <a
                      href={CAL_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sv-btn sv-btn-primary"
                      data-testid={`book-call-${service.id}`}
                    >
                      Book Your Call <ArrowRight size={16} />
                    </a>
                  )}
                  <a
                    href={CAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sv-card-call"
                  >
                    <Phone size={14} />
                    <span>{service.paymentLink ? 'Questions? Schedule a call' : 'Schedule a call to learn more'}</span>
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* RECOMMENDATION */}
      <section className="sv-reco-section">
        <div className="sv-reco-head">
          <span className="sv-eyebrow">Not sure where to start?</span>
          <h2 className="sv-section-title">Choose the room that matches your season.</h2>
        </div>
        <div className="sv-reco-grid">
          <div className="sv-reco-card">
            <h3>If you're starting fresh</h3>
            <p>Begin with the <strong>Business Formation Package</strong> or the <strong>90-Day Funding Readiness</strong> to lay the right foundation.</p>
            <Link to="/contact" className="sv-reco-link">Schedule Consultation <ArrowRight size={14} /></Link>
          </div>
          <div className="sv-reco-card">
            <h3>If you're rebuilding</h3>
            <p>Our <strong>Credit Repair</strong> and <strong>Student Loan Removal</strong> services clean your file so lenders say yes.</p>
            <Link to="/contact" className="sv-reco-link">Schedule Consultation <ArrowRight size={14} /></Link>
          </div>
          <div className="sv-reco-card sv-reco-highlight">
            <span className="sv-reco-badge">The Flagship</span>
            <h3>If you want complete structure</h3>
            <p>The <strong>Wealth Rebuild System™</strong> stacks credit, loans, tradelines, entity, and funding under one roof.</p>
            <Link to="/wealth-rebuild" className="sv-reco-link sv-reco-link-primary">Explore Wealth Rebuild <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>

      {/* HOW WE DELIVER */}
      <section className="sv-process-section">
        <div className="sv-reco-head">
          <span className="sv-eyebrow sv-eyebrow-gold">How We Deliver</span>
          <h2 className="sv-section-title">A four-step playbook, every service.</h2>
        </div>
        <div className="sv-process-grid">
          {[
            { num: '01', title: 'Assessment', body: 'Comprehensive analysis of your current situation and funding goals.' },
            { num: '02', title: 'Strategy', body: 'Custom roadmap designed around your specific needs and timeline.' },
            { num: '03', title: 'Execution', body: 'Done-for-you implementation with dedicated strategist support.' },
            { num: '04', title: 'Results', body: 'Optimized financial structure positioned for funding success.' },
          ].map((step) => (
            <div key={step.num} className="sv-process-card">
              <div className="sv-process-num">{step.num}</div>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="sv-cta-section">
        <div className="sv-cta-card">
          <h2>Ready to pick your room?</h2>
          <p>Schedule a consultation and we'll walk you through the service that fits your season.</p>
          <a href={CAL_URL} target="_blank" rel="noopener noreferrer" className="sv-btn sv-btn-primary sv-btn-xl">
            Schedule Consultation <ArrowRight size={18} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
